/**
 * The celebration's own timezone, chosen rather than typed.
 *
 * A free-text box was wrong here for a reason worth stating: `America/New_York` is an IANA
 * identifier, not a name anybody knows, and every near-miss — `America/NewYork`, `EST`,
 * `Eastern` — is silently invalid. The value is load-bearing (it decides what hour a printed
 * worship aid says), and typing it is the one input method that can produce a plausible-looking
 * wrong answer.
 *
 * The list comes from `Intl.supportedValuesOf('timeZone')`, so it is the browser's own zone
 * database rather than a list here that goes stale when a jurisdiction changes its rules. Grouped
 * by region, because six hundred flat entries is not a chooser either.
 */

import { useMemo } from 'react';

import './primitives.css';

export interface TimezoneSelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
}

/**
 * Every zone the browser knows, grouped by region.
 *
 * Computed once per module rather than per render — it is several hundred strings and a sort, and
 * it cannot change while the page is open.
 */
function allZones(): string[] {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };

  try {
    const zones = intl.supportedValuesOf?.('timeZone');
    if (zones && zones.length > 0) return zones;
  } catch {
    // Falls through to the shortlist below.
  }

  // Older engines have no `supportedValuesOf`. A shortlist is a poor substitute for the full
  // database, but it beats an empty select — and it covers where this application is actually used.
  return [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'America/Puerto_Rico',
    'America/Mexico_City',
    'America/Bogota',
    'America/Lima',
    'America/Santiago',
    'America/Sao_Paulo',
    'America/Buenos_Aires',
    'Europe/Dublin',
    'Europe/London',
    'Europe/Lisbon',
    'Europe/Madrid',
    'Europe/Paris',
    'Europe/Rome',
    'Europe/Vatican',
    'Europe/Berlin',
    'Europe/Warsaw',
    'Africa/Lagos',
    'Africa/Nairobi',
    'Asia/Manila',
    'Asia/Kolkata',
    'Australia/Sydney',
    'UTC',
  ];
}

/** The current offset, so a reader can sanity-check a zone they are unsure of. */
function offsetLabel(zone: string, at: Date): string {
  try {
    const formatted = new Intl.DateTimeFormat('en', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(at);

    return formatted.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

export function TimezoneSelect({
  value,
  onChange,
  disabled = false,
  id,
}: TimezoneSelectProps): React.JSX.Element {
  const groups = useMemo(() => {
    const now = new Date();
    const byRegion = new Map<string, { zone: string; label: string }[]>();

    for (const zone of allZones()) {
      const [region = 'Other'] = zone.split('/');
      const city = zone.includes('/') ? zone.slice(zone.indexOf('/') + 1) : zone;
      const offset = offsetLabel(zone, now);

      const entry = {
        zone,
        // "New York (GMT-4)" reads as a place; "America/New_York" reads as a database key.
        label: `${city.replace(/_/g, ' ').replace(/\//g, ' — ')}${offset ? ` (${offset})` : ''}`,
      };

      const existing = byRegion.get(region);
      if (existing) existing.push(entry);
      else byRegion.set(region, [entry]);
    }

    for (const entries of byRegion.values()) {
      entries.sort((a, b) => a.label.localeCompare(b.label));
    }

    return [...byRegion.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, []);

  // A zone the browser does not list — an old record, or a value from another system. Kept as an
  // option rather than silently reset to something else: quietly moving a celebration's timezone
  // would move the celebration.
  const isKnown = groups.some(([, entries]) => entries.some((e) => e.zone === value));

  return (
    <select
      id={id}
      className="tz-select"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {value && !isKnown ? <option value={value}>{value}</option> : null}

      {groups.map(([region, entries]) => (
        <optgroup key={region} label={region.replace(/_/g, ' ')}>
          {entries.map((entry) => (
            <option key={entry.zone} value={entry.zone}>
              {entry.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
